/** CSV export of the download & conversion statistics (Excel-friendly, ';' separated). */

export interface CsvAttribution {
  total: number;
  attributed: number;
  visitors: number;
  medianHours: number | null;
}

export interface CsvStats {
  days: number;
  totals: {
    downloads: number;
    downloadVisitors: number;
    convertedVisitors: number;
    conversionRate: number;
  };
  bookings: CsvAttribution;
  enquiries: CsvAttribution;
  files: { file_slug: string; downloads: number; visitors: number; converted: number; conversionRate: number }[];
  languages: { language: string; downloads: number; visitors: number; share: number }[];
  daily: { date: string; downloads: number; visitors: number; files: Record<string, number> }[];
}

const esc = (value: string | number | null | undefined): string => {
  const s = value === null || value === undefined ? "" : String(value);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const row = (cells: (string | number | null | undefined)[]) => cells.map(esc).join(";");
const num = (n: number, digits = 1) => n.toFixed(digits).replace(".", ",");

export const buildDownloadCsv = (
  stats: CsvStats,
  labels: { files: Record<string, string>; languages: Record<string, string> },
): string => {
  const to = new Date();
  const from = new Date(to.getTime() - stats.days * 24 * 60 * 60 * 1000);
  const d = (x: Date) => x.toISOString().slice(0, 10);
  const fileLabel = (slug: string) => labels.files[slug] ?? slug;
  const hours = (h: number | null) => (h === null ? "" : num(h));

  const lines: string[] = [];

  lines.push(row(["EAP Abutments – Download- und Conversion-Auswertung"]));
  lines.push(row(["Zeitraum von", d(from), "bis", d(to), `${stats.days} Tage`]));
  lines.push(row(["Export erstellt", new Date().toISOString()]));
  lines.push("");

  lines.push(row(["Kennzahl", "Wert", "Einheit"]));
  lines.push(row(["Downloads", stats.totals.downloads, "Anzahl"]));
  lines.push(row(["Downloader (Besucher)", stats.totals.downloadVisitors, "Anzahl"]));
  lines.push(row(["Konvertierte Besucher", stats.totals.convertedVisitors, "Anzahl"]));
  lines.push(row(["Conversion-Rate", num(stats.totals.conversionRate), "%"]));
  lines.push("");

  lines.push(row(["Conversion-Typ", "Gesamt", "nach Download", "Besucher", "Median Zeitspanne (h)"]));
  lines.push(
    row([
      "Videocall-Buchungen",
      stats.bookings.total,
      stats.bookings.attributed,
      stats.bookings.visitors,
      hours(stats.bookings.medianHours),
    ]),
  );
  lines.push(
    row([
      "Kontaktanfragen",
      stats.enquiries.total,
      stats.enquiries.attributed,
      stats.enquiries.visitors,
      hours(stats.enquiries.medianHours),
    ]),
  );
  lines.push("");

  lines.push(row(["Pro Datei"]));
  lines.push(row(["Datei", "Dateikennung", "Downloads", "Besucher", "Conversions", "Conversion-Rate (%)"]));
  for (const f of stats.files) {
    lines.push(
      row([fileLabel(f.file_slug), f.file_slug, f.downloads, f.visitors, f.converted, num(f.conversionRate)]),
    );
  }
  lines.push("");

  lines.push(row(["Pro Sprache"]));
  lines.push(row(["Sprache", "Code", "Downloads", "Besucher", "Anteil (%)"]));
  for (const l of stats.languages) {
    lines.push(
      row([labels.languages[l.language] ?? l.language.toUpperCase(), l.language, l.downloads, l.visitors, num(l.share)]),
    );
  }
  lines.push("");

  // Per day, one column per file so the breakdown stays sortable in Excel.
  const slugs = Array.from(
    new Set([...stats.files.map((f) => f.file_slug), ...stats.daily.flatMap((day) => Object.keys(day.files))]),
  );
  lines.push(row(["Pro Tag (inkl. Dateiaufschlüsselung)"]));
  lines.push(row(["Datum", "Downloads", "Besucher", ...slugs.map(fileLabel)]));
  for (const day of stats.daily) {
    lines.push(row([day.date, day.downloads, day.visitors, ...slugs.map((s) => day.files[s] ?? 0)]));
  }

  return lines.join("\r\n");
};

export const downloadCsvFile = (csv: string, filename: string) => {
  // BOM so Excel detects UTF-8 (umlauts).
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
